import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Plus, Search, Edit2, TrendingUp, TrendingDown } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface Fund {
  id: string;
  fund_name: string;
  fund_type: string;
  current_balance: number;
  allocated_amount: number;
  utilized_amount: number;
  remaining_amount: number;
  description?: string;
  status: string;
}

export default function FundManages() {
  const { formatCurrency } = useCurrency();
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    fund_name: '',
    fund_type: 'operational',
    allocated_amount: '',
    description: '',
  });

  useEffect(() => {
    loadFunds();
  }, []);

  const loadFunds = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setFunds(getMockFunds());
        setLoading(false);
        return;
      }

      const { data, error } = await supabase
        .from('funds')
        .select('*')
        .order('fund_name', { ascending: true });

      if (error) throw error;
      setFunds(data || []);
    } catch (error) {
      console.error('Error loading funds:', error);
      setFunds(getMockFunds());
    } finally {
      setLoading(false);
    }
  };

  const getMockFunds = (): Fund[] => {
    return [
      {
        id: '1',
        fund_name: 'Operational Fund',
        fund_type: 'operational',
        current_balance: 2500000,
        allocated_amount: 3000000,
        utilized_amount: 500000,
        remaining_amount: 2500000,
        description: 'General operational expenses',
        status: 'active',
      },
      {
        id: '2',
        fund_name: 'Emergency Fund',
        fund_type: 'emergency',
        current_balance: 1000000,
        allocated_amount: 1000000,
        utilized_amount: 0,
        remaining_amount: 1000000,
        description: 'Emergency medical supplies and equipment',
        status: 'active',
      },
      {
        id: '3',
        fund_name: 'Development Fund',
        fund_type: 'development',
        current_balance: 1800000,
        allocated_amount: 2000000,
        utilized_amount: 200000,
        remaining_amount: 1800000,
        description: 'Infrastructure development and expansion',
        status: 'active',
      },
      {
        id: '4',
        fund_name: 'Research Fund',
        fund_type: 'research',
        current_balance: 500000,
        allocated_amount: 500000,
        utilized_amount: 0,
        remaining_amount: 500000,
        description: 'Medical research and training',
        status: 'active',
      },
    ];
  };

  const filteredFunds = funds.filter(fund => {
    const matchesSearch = 
      fund.fund_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      fund.fund_type.toLowerCase().includes(searchTerm.toLowerCase());
    return matchesSearch;
  });

  const totalAllocated = filteredFunds.reduce((sum, fund) => sum + fund.allocated_amount, 0);
  const totalUtilized = filteredFunds.reduce((sum, fund) => sum + fund.utilized_amount, 0);
  const totalRemaining = filteredFunds.reduce((sum, fund) => sum + fund.remaining_amount, 0);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    try {
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        alert('Fund created successfully (mock mode)');
        setShowForm(false);
        setFormData({
          fund_name: '',
          fund_type: 'operational',
          allocated_amount: '',
          description: '',
        });
        return;
      }

      const { error } = await supabase
        .from('funds')
        .insert([{
          ...formData,
          current_balance: formData.allocated_amount,
          utilized_amount: 0,
          remaining_amount: formData.allocated_amount,
          status: 'active',
        }]);
      
      if (error) throw error;

      await loadFunds();
      setShowForm(false);
      setFormData({
        fund_name: '',
        fund_type: 'operational',
        allocated_amount: '',
        description: '',
      });
    } catch (error) {
      console.error('Error creating fund:', error);
      alert('Error creating fund');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Fund Manages</span>
          </div>
          <button
            onClick={() => setShowForm(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
          >
            <Plus className="w-4 h-4" />
            <span>Add Fund</span>
          </button>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        {showForm && (
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
            <h2 className="text-xl font-bold text-gray-900 mb-4">Create New Fund</h2>
            <form onSubmit={handleSubmit}>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fund Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.fund_name}
                    onChange={(e) => setFormData({ ...formData, fund_name: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Fund Type *
                  </label>
                  <select
                    required
                    value={formData.fund_type}
                    onChange={(e) => setFormData({ ...formData, fund_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  >
                    <option value="operational">Operational</option>
                    <option value="emergency">Emergency</option>
                    <option value="development">Development</option>
                    <option value="research">Research</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Allocated Amount *
                  </label>
                  <input
                    type="number"
                    required
                    value={formData.allocated_amount}
                    onChange={(e) => setFormData({ ...formData, allocated_amount: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3">
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
                >
                  Create Fund
                </button>
              </div>
            </form>
          </div>
        )}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Allocated</p>
                <p className="text-2xl font-bold text-blue-600 mt-1">{formatCurrency(totalAllocated)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-blue-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Utilized</p>
                <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalUtilized)}</p>
              </div>
              <TrendingDown className="w-8 h-8 text-red-600" />
            </div>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Remaining</p>
                <p className="text-2xl font-bold text-green-600 mt-1">{formatCurrency(totalRemaining)}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-green-600" />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
            <input
              type="text"
              placeholder="Search funds..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Fund Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Allocated
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Utilized
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Remaining
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredFunds.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="px-6 py-4 text-center text-gray-500">
                      No funds found
                    </td>
                  </tr>
                ) : (
                  filteredFunds.map((fund) => (
                    <tr key={fund.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {fund.fund_name}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          fund.fund_type === 'operational' ? 'bg-blue-100 text-blue-800' :
                          fund.fund_type === 'emergency' ? 'bg-red-100 text-red-800' :
                          fund.fund_type === 'development' ? 'bg-green-100 text-green-800' :
                          'bg-purple-100 text-purple-800'
                        }`}>
                          {fund.fund_type}
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-blue-600 font-medium">
                        {formatCurrency(fund.allocated_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-medium">
                        {formatCurrency(fund.utilized_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600 font-bold">
                        {formatCurrency(fund.remaining_amount)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                          fund.status === 'active' ? 'bg-green-100 text-green-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {fund.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
